import { defineStore } from 'pinia';
import api from '@/api';

export const useRecipeStore = defineStore('recipe', {
  state: () => ({
    // Recipe versions list with pagination
    versions: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 50,
    totalPages: 0,

    // Single recipe version
    version: null,

    // Component recipe versions (for a specific component)
    componentVersions: [],

    // Current recipe version for a component
    currentVersion: null,

    // Recipe version lookups for dropdowns
    versionLookups: [],

    // Line items for a recipe version
    lineItems: [],

    // Single line item
    lineItem: null,

    // Where-used data
    whereUsed: null,

    // Loading states
    loading: false,
    lineItemsLoading: false,
    whereUsedLoading: false,

    // Error state
    error: null
  }),

  getters: {
    getVersions: (state) => state.versions,
    getVersion: (state) => state.version,
    getComponentVersions: (state) => state.componentVersions,
    getCurrentVersion: (state) => state.currentVersion,
    getVersionLookups: (state) => state.versionLookups,
    getLineItems: (state) => state.lineItems,
    getLineItem: (state) => state.lineItem,
    getWhereUsed: (state) => state.whereUsed,
    getPagination: (state) => ({
      totalCount: state.totalCount,
      pageNumber: state.pageNumber,
      pageSize: state.pageSize,
      totalPages: state.totalPages
    }),
    isLoading: (state) => state.loading,
    isLineItemsLoading: (state) => state.lineItemsLoading,
    isWhereUsedLoading: (state) => state.whereUsedLoading,
    getError: (state) => state.error
  },

  actions: {
    // ==================== Recipe Versions ====================

    /**
     * Fetch recipe versions with pagination
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive]
     * @param {number} [params.pageNumber]
     * @param {number} [params.pageSize]
     */
    async fetchVersions(params = {}) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.recipe.getRecipeVersions(params);
        this.versions = resp.data.versions || [];
        this.totalCount = resp.data.totalCount;
        this.pageNumber = resp.data.pageNumber;
        this.pageSize = resp.data.pageSize;
        this.totalPages = resp.data.totalPages;
      } catch (e) {
        this.error = e;
        this.versions = [];
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch single recipe version by ID
     * @param {number} id
     * @param {Object} [params]
     * @param {boolean} [params.includeLineItems]
     */
    async fetchVersion(id, params = {}) {
      this.loading = true;
      this.error = null;
      // Clear immediately, not just on error - a detail view watching
      // route.params.id would otherwise briefly show the previous entity's
      // data (both the version header AND its line items - RecipeDetails.vue
      // renders lineItems as the actual ingredient rows on screen).
      this.version = null;
      this.lineItems = [];
      try {
        const resp = await api.recipe.getRecipeVersion(id, params);
        this.version = resp.data;
        // Also update lineItems if included
        if (resp.data.lineItems) {
          this.lineItems = resp.data.lineItems;
        }
      } catch (e) {
        this.error = e;
        this.version = null;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Create new recipe version
     * @param {Object} payload
     */
    async createVersion(payload) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.recipe.createRecipeVersion(payload);
        this.version = resp.data;
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Update recipe version
     * @param {number} id
     * @param {Object} payload
     */
    async updateVersion(id, payload) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.recipe.updateRecipeVersion(id, payload);
        this.version = resp.data;
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Delete recipe version (soft delete)
     * @param {number} id
     */
    async deleteVersion(id) {
      this.loading = true;
      this.error = null;
      try {
        await api.recipe.deleteRecipeVersion(id);
        this.version = null;
        // Remove from both lists it could be showing in - no need to
        // refetch just to drop one row we already know is gone.
        this.versions = this.versions.filter(v => v.recipeVersionId !== id);
        this.componentVersions = this.componentVersions.filter(v => v.recipeVersionId !== id);
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch all recipe versions for a component
     * @param {number} componentId
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive]
     */
    async fetchComponentVersions(componentId, params = {}) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.recipe.getComponentRecipeVersions(componentId, params);
        this.componentVersions = resp.data || [];
      } catch (e) {
        this.error = e;
        this.componentVersions = [];
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch current recipe version for a component
     * @param {number} componentId
     */
    async fetchCurrentVersion(componentId) {
      this.loading = true;
      this.error = null;
      this.currentVersion = null;
      try {
        const resp = await api.recipe.getCurrentRecipeVersion(componentId);
        this.currentVersion = resp.data;
      } catch (e) {
        this.error = e;
        this.currentVersion = null;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch recipe version lookups for dropdowns
     * @param {Object} [params]
     * @param {number} [params.componentId]
     */
    async fetchVersionLookups(params = {}) {
      this.error = null;
      try {
        const resp = await api.recipe.getRecipeVersionLookups(params);
        this.versionLookups = resp.data || [];
      } catch (e) {
        this.error = e;
        this.versionLookups = [];
        throw e;
      }
    },

    /**
     * Set a recipe version as current
     * @param {number} id
     */
    async setCurrentVersion(id) {
      this.loading = true;
      this.error = null;
      try {
        await api.recipe.setCurrentVersion(id);
        // Update local state
        if (this.version && this.version.recipeVersionId === id) {
          this.version.isCurrentVersion = true;
        }
        // Update in list
        this.versions = this.versions.map(v => ({
          ...v,
          isCurrentVersion: v.recipeVersionId === id
        }));
        this.componentVersions = this.componentVersions.map(v => ({
          ...v,
          isCurrentVersion: v.recipeVersionId === id
        }));
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Copy a recipe version
     * @param {number} id
     * @param {string} [newVersionName]
     */
    async copyVersion(id, newVersionName) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.recipe.copyRecipeVersion(id, newVersionName);
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    // ==================== Recipe Line Items ====================

    /**
     * Fetch line items for a recipe version
     * @param {number} versionId
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive]
     */
    async fetchLineItems(versionId, params = {}) {
      this.lineItemsLoading = true;
      this.error = null;
      try {
        const resp = await api.recipe.getLineItems(versionId, params);
        this.lineItems = resp.data || [];
      } catch (e) {
        this.error = e;
        this.lineItems = [];
        throw e;
      } finally {
        this.lineItemsLoading = false;
      }
    },

    /**
     * Fetch single line item by ID
     * @param {number} id
     */
    async fetchLineItem(id) {
      this.lineItemsLoading = true;
      this.error = null;
      // Clear immediately, not just on error - a detail view watching
      // route.params.id would otherwise briefly show the previous entity.
      this.lineItem = null;
      try {
        const resp = await api.recipe.getLineItem(id);
        this.lineItem = resp.data;
      } catch (e) {
        this.error = e;
        this.lineItem = null;
        throw e;
      } finally {
        this.lineItemsLoading = false;
      }
    },

    /**
     * Create line item for a recipe version
     * @param {number} versionId
     * @param {Object} payload
     */
    async createLineItem(versionId, payload) {
      this.lineItemsLoading = true;
      this.error = null;
      try {
        const resp = await api.recipe.createLineItem(versionId, payload);
        this.lineItem = resp.data;
        // Add to list
        this.lineItems.push(resp.data);
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.lineItemsLoading = false;
      }
    },

    /**
     * Update line item
     * @param {number} id
     * @param {Object} payload
     */
    async updateLineItem(id, payload) {
      this.lineItemsLoading = true;
      this.error = null;
      try {
        const resp = await api.recipe.updateLineItem(id, payload);
        this.lineItem = resp.data;
        // Update in list
        const idx = this.lineItems.findIndex(li => li.recipeId === id);
        if (idx !== -1) {
          this.lineItems[idx] = resp.data;
        }
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.lineItemsLoading = false;
      }
    },

    /**
     * Delete line item (soft delete)
     * @param {number} id
     */
    async deleteLineItem(id) {
      this.lineItemsLoading = true;
      this.error = null;
      try {
        await api.recipe.deleteLineItem(id);
        this.lineItem = null;
        // Remove from list
        this.lineItems = this.lineItems.filter(li => li.recipeId !== id);
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.lineItemsLoading = false;
      }
    },

    // ==================== Where Used ====================

    /**
     * Fetch where a component is used
     * @param {number} componentId
     * @param {Object} [params]
     * @param {boolean} [params.currentVersionOnly]
     */
    async fetchWhereUsed(componentId, params = {}) {
      this.whereUsedLoading = true;
      this.error = null;
      this.whereUsed = null;
      try {
        const resp = await api.recipe.getWhereUsed(componentId, params);
        this.whereUsed = resp.data;
      } catch (e) {
        this.error = e;
        this.whereUsed = null;
        throw e;
      } finally {
        this.whereUsedLoading = false;
      }
    },

    // ==================== Clear Methods ====================

    clearVersions() {
      this.versions = [];
      this.totalCount = 0;
      this.pageNumber = 1;
      this.totalPages = 0;
    },

    clearVersion() {
      this.version = null;
    },

    clearComponentVersions() {
      this.componentVersions = [];
    },

    clearCurrentVersion() {
      this.currentVersion = null;
    },

    clearVersionLookups() {
      this.versionLookups = [];
    },

    clearLineItems() {
      this.lineItems = [];
    },

    clearLineItem() {
      this.lineItem = null;
    },

    clearWhereUsed() {
      this.whereUsed = null;
    },

    clearError() {
      this.error = null;
    },

    clearAll() {
      this.clearVersions();
      this.clearVersion();
      this.clearComponentVersions();
      this.clearCurrentVersion();
      this.clearVersionLookups();
      this.clearLineItems();
      this.clearLineItem();
      this.clearWhereUsed();
      this.clearError();
    }
  }
});
